import { useEffect, useMemo, useState } from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  Bars3Icon,
  CheckIcon,
  ArrowUturnLeftIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  useCatalogScopes,
  useCatalogOrder,
  useSaveCatalogOrder,
  useResyncCatalog,
} from '../hooks/useStockCatalogOrder';

// Arranges the stock forms engineers fill in from a job card — the job card
// equivalent of the SKU manager's form order.
//
// Each form is arranged independently because they never render together: the
// install kit is split by region, and a Remediation card draws a completely
// different catalog. OG/UG is NOT a separate form — it filters items within
// one, so the overhead-only categories (Eyebolts, Brackets) are arranged here
// once and simply don't appear on an underground job.

const SCOPES = [
  { key: 'install-N',     label: 'Installs — Region N',      discipline: 'install', region: 'N', job_type: null },
  { key: 'install-N-rem', label: 'Remediation — Region N',   discipline: 'install', region: 'N', job_type: 'Remediation' },
  { key: 'install-B',     label: 'Installs — Region B',      discipline: 'install', region: 'B', job_type: null },
  { key: 'install-B-rem', label: 'Remediation — Region B',   discipline: 'install', region: 'B', job_type: 'Remediation' },
  { key: 'civils-N',      label: 'Civils — Region N',        discipline: 'civils',  region: 'N', job_type: null },
];

function move(list, from, to) {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

// Groups mirror exactly what the engineer form renders. is_consumable leads the
// form's sort, so it's part of the group key and categories can only move
// within their own block — letting a consumable category be dragged above an
// ISP one would show a preview that the form can never actually produce.
function buildGroups(items) {
  const groups = new Map();
  for (const item of items) {
    const key = `${item.is_consumable ? 1 : 0}|${item.category || ''}`;
    if (!groups.has(key)) {
      groups.set(key, { key, category: item.category || '', is_consumable: item.is_consumable ? 1 : 0, items: [] });
    }
    groups.get(key).items.push(item);
  }
  return [...groups.values()];
}

function ItemRow({ item, index, total, onMove, onDragStart, onDragOver, onDrop, dragging, onForm, onToggle }) {
  return (
    <li
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => { e.preventDefault(); onDragOver(index); }}
      onDrop={(e) => { e.preventDefault(); onDrop(); }}
      className={`flex items-center gap-2 border-t border-gray-100 px-3 py-2 first:border-t-0 ${
        dragging ? 'opacity-40' : ''
      } ${onForm ? 'bg-white' : 'bg-gray-50'}`}
    >
      {/* Hidden items stay in the list, and keep their position, so switching
          one back on returns it to where it was rather than the end. */}
      <input
        type="checkbox"
        checked={onForm}
        onChange={() => onToggle(item.id)}
        aria-label={`Show ${item.item_name} on the form`}
        className="h-4 w-4 shrink-0 rounded border-gray-300 text-brand focus:ring-brand"
      />
      <Bars3Icon className="h-4 w-4 shrink-0 cursor-grab text-gray-300" aria-hidden="true" />
      <span className="w-8 shrink-0 text-xs tabular-nums text-gray-400">{index + 1}</span>
      <span className={`min-w-0 flex-1 truncate text-sm ${onForm ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
        {item.display_label || item.item_name}
        <span className="ml-2 text-xs text-gray-400 no-underline">{item.sku}</span>
      </span>
      {item.install_type && item.install_type !== 'BOTH' ? (
        <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
          {item.install_type === 'OG' ? 'Overhead only' : 'Underground only'}
        </span>
      ) : null}
      {item.unit === 'per_meter' ? (
        <span className="shrink-0 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-800">per m</span>
      ) : null}
      <div className="flex shrink-0 gap-1">
        <button
          type="button"
          onClick={() => onMove(index, index - 1)}
          disabled={index === 0}
          aria-label={`Move ${item.item_name} up`}
          className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-25"
        >
          <ArrowUpIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onMove(index, index + 1)}
          disabled={index === total - 1}
          aria-label={`Move ${item.item_name} down`}
          className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-25"
        >
          <ArrowDownIcon className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

function JobCardStockOrder() {
  const [scopeKey, setScopeKey] = useState(SCOPES[0].key);
  const scope = SCOPES.find((s) => s.key === scopeKey);
  const scopesQuery = useCatalogScopes();
  const { data, isLoading, isError, error } = useCatalogOrder(scope);
  const save = useSaveCatalogOrder(scope);

  const resync = useResyncCatalog();

  const initial = useMemo(() => buildGroups(data?.items || []), [data]);
  const initialOff = useMemo(
    () => new Set((data?.items || []).filter((i) => !i.include_in_job_form).map((i) => i.id)),
    [data]
  );
  const [groups, setGroups] = useState(initial);
  const [off, setOff] = useState(initialOff);
  const [drag, setDrag] = useState(null); // { groupIndex, from, over }

  // Reset whenever the form changes underneath, so an unsaved arrangement for
  // Region N can't be saved onto Region B.
  useEffect(() => {
    setGroups(initial);
    setOff(initialOff);
    setDrag(null);
    save.reset();
    // Only `initial` should retrigger this — depending on the mutation object
    // would clear the "Saved" banner the moment it appeared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial, initialOff]);

  const dirty = useMemo(() => {
    const flat = (gs) => gs.map((g) => `${g.key}:${g.items.map((i) => i.id).join(',')}`).join('|');
    const sameOff = off.size === initialOff.size && [...off].every((id) => initialOff.has(id));
    return flat(groups) !== flat(initial) || !sameOff;
  }, [groups, initial, off, initialOff]);

  const toggleItem = (id) =>
    setOff((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  // A category can only swap with a neighbour in the same ISP/consumable block.
  const moveCategory = (from, to) => setGroups((gs) => {
    if (to < 0 || to >= gs.length) return gs;
    if (gs[from].is_consumable !== gs[to].is_consumable) return gs;
    return move(gs, from, to);
  });

  const moveItem = (groupIndex, from, to) =>
    setGroups((gs) => gs.map((g, i) => (i === groupIndex ? { ...g, items: move(g.items, from, to) } : g)));

  const handleDrop = () => {
    if (drag && drag.over !== null && drag.over !== drag.from) moveItem(drag.groupIndex, drag.from, drag.over);
    setDrag(null);
  };

  const handleSave = () =>
    save.mutate({
      categories: groups.map((g) => ({ category: g.category, itemIds: g.items.map((i) => i.id) })),
      offFormIds: [...off],
    });

  const counts = useMemo(() => {
    const map = {};
    for (const r of scopesQuery.data?.install || []) {
      map[`install-${r.region}${r.job_type ? '-rem' : ''}`] = r.items;
    }
    map['civils-N'] = scopesQuery.data?.civils ?? null;
    return map;
  }, [scopesQuery.data]);

  const totalItems = groups.reduce((n, g) => n + g.items.length, 0);
  const canMoveUp = (i) => i > 0 && groups[i - 1].is_consumable === groups[i].is_consumable;
  const canMoveDown = (i) => i < groups.length - 1 && groups[i + 1].is_consumable === groups[i].is_consumable;

  return (
    <div className="p-6">
      <h1 className="text-xl font-semibold text-gray-900">Job card stock — form order</h1>
      <p className="mt-1 text-sm text-gray-500">
        What engineers see, and in what order, when they log stock against a job card. Each form is
        arranged separately. Overhead-only items are shown here but only appear on overhead jobs.
        Items themselves are added and removed in the SKU managers — untick one here to keep it in
        the manager but off the job card.
      </p>

      {/* Form picker */}
      <div className="mt-4 flex flex-wrap gap-2">
        {SCOPES.map((s) => {
          const n = counts[s.key];
          const active = s.key === scopeKey;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setScopeKey(s.key)}
              className={`rounded-md border px-3 py-2 text-sm font-medium ${
                active
                  ? 'border-brand bg-brand text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {s.label}
              {n != null ? (
                <span className={`ml-2 text-xs ${active ? 'text-white/75' : 'text-gray-400'}`}>{n}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Leaving the page loses the arrangement, and a long reorder is real
          work to redo — so warn rather than autosave, which would change the
          form under an engineer who is mid-job. */}
      {dirty ? (
        <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          Unsaved changes — engineers still see the old order until you save.
        </p>
      ) : null}

      {save.isError ? (
        <p className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {save.error?.response?.data?.error || 'Could not save the order. Nothing has been changed.'}
        </p>
      ) : null}
      {save.isSuccess && !dirty ? (
        <p className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
          Saved — engineers see this order now.
        </p>
      ) : null}

      {resync.isError ? (
        <p className="mt-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {resync.error?.response?.data?.error || 'Could not re-sync the catalog.'}
        </p>
      ) : null}
      {resync.isSuccess ? (
        <p className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
          Re-synced from the SKU managers — {resync.data?.install ?? 0} install and{' '}
          {resync.data?.civils ?? 0} civils items. Your order and hidden items were kept.
        </p>
      ) : null}

      <div className="sticky top-0 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-xs text-gray-500">
          {totalItems - off.size} of {totalItems} item{totalItems === 1 ? '' : 's'} on the form, in{' '}
          {groups.length} categor{groups.length === 1 ? 'y' : 'ies'}. Drag or use the arrows to
          reorder; untick to take an item off the form.
        </p>
        <div className="flex items-center gap-2">
          {/* Items come from the SKU managers, so adding/removing one happens
              there — this pulls those edits through without waiting for the
              overnight sync. */}
          <button
            type="button"
            onClick={() => resync.mutate()}
            disabled={resync.isPending || dirty}
            title={dirty ? 'Save or reset your changes first' : 'Pull new and changed SKUs from the stock managers'}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${resync.isPending ? 'animate-spin' : ''}`} />
            {resync.isPending ? 'Syncing...' : 'Re-sync from SKU managers'}
          </button>
          {dirty ? (
            <button
              type="button"
              onClick={() => setGroups(initial)}
              className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-white"
            >
              <ArrowUturnLeftIcon className="h-4 w-4" />
              Reset
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            disabled={!dirty || save.isPending}
            className="flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-50"
          >
            <CheckIcon className="h-4 w-4" />
            {save.isPending ? 'Saving...' : 'Save order'}
          </button>
        </div>
      </div>

      {isLoading ? <p className="mt-6 text-sm text-gray-400">Loading…</p> : null}
      {isError ? (
        <p className="mt-6 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">
          {error?.response?.data?.error || 'Could not load this form.'}
        </p>
      ) : null}

      <div className="mt-4 space-y-3">
        {!isLoading && groups.length === 0 ? (
          <p className="rounded-md border border-gray-200 bg-white px-4 py-6 text-center text-sm text-gray-400">
            This form has no stock items yet. Add them in the SKU managers, then re-sync the catalog.
          </p>
        ) : null}

        {groups.map((group, groupIndex) => {
          const startsConsumables =
            group.is_consumable === 1 && (groupIndex === 0 || groups[groupIndex - 1].is_consumable === 0);
          return (
            <div key={group.key}>
              {startsConsumables ? (
                <h2 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Consumables — always shown after ISP stock on the form
                </h2>
              ) : null}
              <div className="overflow-hidden rounded-md border border-gray-200">
                <div className="flex items-center gap-2 bg-gray-100 px-3 py-2">
                  <span className="w-8 shrink-0 text-xs tabular-nums text-gray-400">{groupIndex + 1}</span>
                  <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-800">
                    {group.category || <span className="italic text-gray-500">Uncategorised</span>}
                    <span className="ml-2 font-normal text-gray-400">({group.items.length})</span>
                  </h3>
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => moveCategory(groupIndex, groupIndex - 1)}
                      disabled={!canMoveUp(groupIndex)}
                      aria-label={`Move category ${group.category || 'Uncategorised'} up`}
                      className="rounded p-1 text-gray-600 hover:bg-white disabled:opacity-25"
                    >
                      <ArrowUpIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveCategory(groupIndex, groupIndex + 1)}
                      disabled={!canMoveDown(groupIndex)}
                      aria-label={`Move category ${group.category || 'Uncategorised'} down`}
                      className="rounded p-1 text-gray-600 hover:bg-white disabled:opacity-25"
                    >
                      <ArrowDownIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <ul>
                  {group.items.map((item, index) => (
                    <ItemRow
                      key={item.id}
                      item={item}
                      index={index}
                      total={group.items.length}
                      dragging={drag?.groupIndex === groupIndex && drag?.from === index}
                      onForm={!off.has(item.id)}
                      onToggle={toggleItem}
                      onMove={(from, to) => moveItem(groupIndex, from, to)}
                      onDragStart={(from) => setDrag({ groupIndex, from, over: from })}
                      onDragOver={(over) => {
                        // Dragging is confined to one category: an item's
                        // category comes from the SKU manager, so dropping it
                        // into another group would imply a recategorisation
                        // this screen isn't for. Move the category instead.
                        if (drag?.groupIndex === groupIndex) setDrag((d) => ({ ...d, over }));
                      }}
                      onDrop={handleDrop}
                    />
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default JobCardStockOrder;
